<script setup lang="ts">
    import { ref } from 'vue';
    import { TaskError, TaskErrorStatus } from './HealthManager.types';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';

    const filterValue = ref('');

    const props = defineProps<{
        taskErrors: TaskError[];
        loading: boolean;
        totalItems: number;
        pageSize: number;
        currentPage: number;
    }>();

    const emit = defineEmits<{
        (e: 'pageChanged', page: number): void;
        (e: 'filterChanged', filter: string): void;
        (e: 'switchAccount', accountId: string): void;
    }>();

    const statusLabel = (status: TaskErrorStatus): string => TaskErrorStatus[status];

    const statusClass = (status: TaskErrorStatus): string => {
        switch (status) {
            case TaskErrorStatus.New:
                return 'badge badge-danger';
            case TaskErrorStatus.InProgress:
                return 'badge badge-warning';
            case TaskErrorStatus.Postponed:
                return 'badge badge-secondary';
            case TaskErrorStatus.Solved:
                return 'badge badge-success';
            default:
                return 'badge';
        }
    };
</script>

<template>
    <div>
        <h3>Last Modified</h3>

        <div class="m-md-b" style="max-width: 300px">
            <BSFormSearch
                v-model="filterValue"
                inputId="last-modified-search"
                :debounce-time="400"
                :placeholder="'Filter by account name'"
                @reset="() => { filterValue = ''; emit('filterChanged', ''); }"
                @search="() => emit('filterChanged', filterValue)" />
        </div>

        <div class="table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th>Last Modified</th>
                        <th>Error Code</th>
                        <th>Task Type</th>
                        <th>Account</th>
                        <th>Status</th>
                        <th>Owned By</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!loading && taskErrors.length === 0">
                        <td colspan="6" class="text-center">No recently modified task errors found.</td>
                    </tr>
                    <tr v-for="taskError in taskErrors" :key="taskError.TaskErrorId">
                        <td>{{ formatDateTime(taskError.LastModified) }}</td>
                        <td v-tooltip="taskError.Error">{{ taskError.ErrorCode }}</td>
                        <td>
                            <span>{{ taskError.Task?.TypeName }}</span>
                        </td>
                        <td>
                            <a href="javascript:void(0)" role="button" @click="emit('switchAccount', taskError.Account.AccountId)">
                                {{ taskError.Account?.Name }}
                            </a>
                        </td>
                        <td>
                            <span :class="statusClass(taskError.Status)">{{ statusLabel(taskError.Status) }}</span>
                        </td>
                        <td>{{ taskError.OwnedBy?.Name ?? '—' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <nav v-if="totalItems > pageSize" aria-label="Last modified pagination">
            <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button class="page-link" @click="emit('pageChanged', currentPage - 1)">Previous</button>
                </li>
                <li
                    v-for="page in Math.ceil(totalItems / pageSize)"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === currentPage }">
                    <button class="page-link" @click="emit('pageChanged', page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= Math.ceil(totalItems / pageSize) }">
                    <button class="page-link" @click="emit('pageChanged', currentPage + 1)">Next</button>
                </li>
            </ul>
        </nav>
    </div>
</template>
